/**
 * Slug utilities. Pure functions, shared by client + server and unit-tested.
 */

/** Convert arbitrary text into a URL-safe slug. */
export function slugify(input: string): string {
  return input
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "") // strip diacritics (combining marks)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-") // non-alphanumerics -> hyphen
    .replace(/^-+|-+$/g, "") // trim leading/trailing hyphens
    .replace(/-{2,}/g, "-") // collapse repeats
    .slice(0, 80)
    .replace(/-+$/g, ""); // re-trim after slice

}

/** True if the slug is well-formed (what the DB/API will accept). */
export function isValidSlug(slug: string): boolean {
  return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug) && slug.length >= 1 && slug.length <= 80;
}

/**
 * Page slugs that a static storefront route already owns, so a page using one
 * could never be reached: `<host>/plan` is the reseller plan-signup page,
 * `<host>/bio` the bio-web's permanent link, and React Router ranks those static
 * segments above the `/:slug` page renderer.
 * Rejecting the slug at save time is the honest failure — the alternative is a
 * page that saves fine and silently 404s forever.
 *
 * Only `plan` and `bio` for now: the other static storefront segments (`checkout`,
 * `product`, …) predate this list and no page uses them; add them here if that
 * ever changes.
 */
export const RESERVED_PAGE_SLUGS: ReadonlySet<string> = new Set(["plan", "bio"]);

/** True if `slug` is usable as a PAGE slug: well-formed and not route-shadowed. */
export function isValidPageSlug(slug: string): boolean {
  return isValidSlug(slug) && !RESERVED_PAGE_SLUGS.has(slug);
}

/**
 * Handles that collide with top-level app routes (or are otherwise reserved)
 * and therefore cannot be used as a team prefix. `p` is the default bucket.
 * Keep in sync with the top-level <Route> segments in src/App.tsx.
 */
export const RESERVED_HANDLES: ReadonlySet<string> = new Set([
  "p",
  "login",
  "register",
  "dashboard",
  "shop",
  "account",
  "learn",
  "access",
  "thank-you",
  "verify-email",
  "api",
  "assets",
  "admin",
  "www",
  "documentation",
  "masuk",
  "k",
]);

/**
 * True if `handle` is a usable team prefix: slug-shaped, 1–30 chars, and not a
 * reserved word. Reuses the slug grammar (lowercase alnum + single hyphens).
 */
export function isValidHandle(handle: string): boolean {
  return (
    /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(handle) &&
    handle.length >= 1 &&
    handle.length <= 30 &&
    !RESERVED_HANDLES.has(handle)
  );
}

/**
 * Subdomain labels reserved for platform infrastructure: they either serve the
 * dashboard/app itself or are conventional service hosts. A seller can't claim
 * one as their store's vanity host, or their storefront would shadow the app.
 * Superset of the app routes (a subdomain fronts the WHOLE app, not one route),
 * plus common infra labels. Keep the app-facing entries in sync with the hosts
 * treated as "the app" in src/lib/host.ts.
 */
export const RESERVED_SUBDOMAINS: ReadonlySet<string> = new Set([
  "www",
  "ebook",
  "app",
  "api",
  "admin",
  "dashboard",
  "account",
  "accounts",
  "auth",
  "login",
  "register",
  "assets",
  "static",
  "cdn",
  "embed",
  "mail",
  "email",
  "smtp",
  "ftp",
  "ns",
  "ns1",
  "ns2",
  "mx",
  "webmail",
  "blog",
  "docs",
  "help",
  "support",
  "status",
  "shop",
  "store",
  "checkout",
  "pay",
  "billing",
  "cpanel",
  "webdisk",
  "autodiscover",
  "test",
  "dev",
  "staging",
]);

/**
 * True if `sub` is a usable store subdomain: a valid DNS label (lowercase alnum
 * + single hyphens), 3–63 chars, and not reserved. The 3-char floor keeps the
 * short, service-like labels (a, io, …) out of sellers' hands.
 */
export function isValidSubdomain(sub: string): boolean {
  return (
    /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(sub) &&
    sub.length >= 3 &&
    sub.length <= 63 &&
    !RESERVED_SUBDOMAINS.has(sub)
  );
}

/**
 * Given a desired slug and a set of slugs already taken, return a unique slug.
 * Appends -2, -3, ... until free. e.g. "nama-page" -> "nama-page-2".
 */
export function suggestUniqueSlug(desired: string, taken: Iterable<string>): string {
  const base = slugify(desired) || "page";
  const used = taken instanceof Set ? taken : new Set(taken);
  if (!used.has(base)) return base;

  let n = 2;
  while (used.has(`${base}-${n}`)) n++;
  return `${base}-${n}`;
}
